import { useMemo } from 'react';
import {
  useBookingLocaleAsGuestQuery,
  useBookingLocaleAsHostQuery,
  useBookingLocaleQuery,
} from '../services/bookingService/__generated__/queries.generated';
import { asCurrency, Currency } from '../utils/money';
import { asDistanceUnit, DistanceUnit } from '../utils/units';

export interface BookingLocale {
  distanceUnit: DistanceUnit;
  currency: Currency;
  /** "HST", "GST" or "Tax" (spec D16). */
  taxLabel: string;
}

type ReceiptLocale = { distanceUnit?: string | null; currency?: string | null } | null | undefined;

/** Spec 028: a trip from before spec 028, or no answer, is miles and USD. */
export function bookingLocaleFrom(receipt: ReceiptLocale, taxLabel?: string | null): BookingLocale {
  return {
    distanceUnit: asDistanceUnit(receipt?.distanceUnit),
    currency: asCurrency(receipt?.currency),
    taxLabel: taxLabel || 'Tax',
  };
}

/**
 * Spec 028: the unit and currency of a trip. Trip pages use these, never the
 * site's current settings, so a later change of the brand never changes a trip.
 */
export function useBookingLocale(
  bookingId: string | null | undefined,
  /** `any` reads through `booking(id)`, for pages that do not know the role. */
  role: 'guest' | 'host' | 'any',
): BookingLocale {
  const skip = !bookingId;
  const guest = useBookingLocaleAsGuestQuery({
    variables: { id: bookingId ?? '' },
    skip: skip || role !== 'guest',
    errorPolicy: 'ignore',
  });
  const host = useBookingLocaleAsHostQuery({
    variables: { id: bookingId ?? '' },
    skip: skip || role !== 'host',
    errorPolicy: 'ignore',
  });
  const any = useBookingLocaleQuery({
    variables: { id: bookingId ?? '' },
    skip: skip || role !== 'any',
    errorPolicy: 'ignore',
  });
  const booking =
    role === 'host'
      ? host.data?.getBookingAsHost
      : role === 'any'
        ? any.data?.booking
        : guest.data?.getBookingAsGuest;
  const receipt = booking?.receipt;
  const taxLabel = booking?.taxLabel;
  return useMemo(() => bookingLocaleFrom(receipt, taxLabel), [receipt, taxLabel]);
}
